import { Field, Form, Formik } from "formik";
import TextField from "pages/_components/fields/TextField";
import GridTable from "pages/_components/GridTable/GridTable";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import React, { useEffect, useState } from "react";
import * as i18n from "util/i18n";
import * as Yup from "yup";
import Selector from "pages/_components/fields/formik/Selector";
import GeneralCredentialForm from "pages/_components/Forms/GeneralCredentialForm";
import { selectors as comexSelectors, actions as comexActions } from "reducers/comex";

import CustomText from "pages/_components/CustomText/CustomText";
import Drawer from "pages/_components/Drawer/Drawer";
import Checkbox from "pages/_components/Checkbox";
import { bool } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { push } from "connected-react-router";
import { connect, useDispatch, useSelector } from "react-redux";
import Button from "pages/_components/Button";
import ComexStepper from "../_components/ComexStepper";

const FORM_ID = "comex.contact";
const MAX_REQUIRED = "34";
const MAX_REQUIRED_ADDRESS = "40";
const MAX_REQUIRED_COUNTRY_CODE = "10";

const ComexNewContactPage = ({ isDesktop, fetching }) => {
    const dispatch = useDispatch();
    const { comexTransfer = {}, comexCurrencies = [], comexCountries = [] } = useSelector((state) => state.comex);
    const contactDestination = comexTransfer.contactDestination ?? {};

    const [showAccountNumberInfo, setShowAccountNumberInfo] = useState(false);
    const [showDestinationInfo, setShowDestinationInfo] = useState(false);
    const [selectedBankType, setSelectedBankType] = useState(null);

    const handleCheckboxChange = (e, setFieldValue, otherField) => {
        const targetCode = otherField == "aba" ? "swift" : "aba";
        setSelectedBankType(targetCode);
        if (!e) setSelectedBankType(null);
        setFieldValue(otherField, false);
    };

    const initialValues = {
        recipientName: contactDestination.recipientName || "",
        iban: contactDestination.iban || "",
        swift: contactDestination?.destination === "SWIFT" || contactDestination?.swift,
        aba: contactDestination?.destination === "ABA" || contactDestination?.aba,
        country: contactDestination.country || "",
        number: contactDestination.number || "",
        currency: contactDestination.currency || "",
        countryCode: contactDestination.countryCode || "",
        address: contactDestination.address || "",
    };

    useEffect(() => {
        dispatch(comexActions.getComexSelectors());
    }, []);

    const newContactSchema = Yup.object()
        .shape({
            recipientName: Yup.string().required(i18n.get(`global.error.required`)),

            iban: Yup.string()
                .max(parseInt(MAX_REQUIRED, 10), `${i18n.get("forms.validation.max.required")} ${MAX_REQUIRED}`)
                .required(i18n.get(`global.error.required`))
                .matches(/^[a-zA-Z0-9]{1,34}$/, i18n.get("global.incorrect.format")),

            swift: Yup.boolean(),
            aba: Yup.boolean(),

            address: Yup.string()
                .max(
                    parseInt(MAX_REQUIRED_ADDRESS, 10),
                    `${i18n.get("forms.validation.max.required")} ${MAX_REQUIRED_ADDRESS}`,
                )
                .required(i18n.get(`global.error.required`)),

            country: Yup.string().required(i18n.get(`global.error.required`)),

            number: Yup.string()
                .required(i18n.get(`global.error.required`))
                .when(["swift", "aba"], {
                    is: (swift, aba) => swift,
                    then: Yup.string().matches(/^[a-zA-Z0-9]{8,11}$/, i18n.get("comex.forms.validation.invalid.swift")),
                })
                .when(["swift", "aba"], {
                    is: (swift, aba) => aba,
                    then: Yup.string().matches(/^\d{9}$/, i18n.get("comex.forms.validation.invalid.aba")),
                }),

            currency: Yup.string().required(i18n.get(`global.error.required`)),

            countryCode: Yup.string()
                .max(
                    parseInt(MAX_REQUIRED_COUNTRY_CODE, 10),
                    `${i18n.get("forms.validation.max.required")} ${MAX_REQUIRED_COUNTRY_CODE}`,
                )
                .required(i18n.get(`global.error.required`)),
        })
        .test("one-required", i18n.get("forms.validation.at.least.one.required"), (values) => {
            const { swift, aba } = values;
            return swift || aba; // Devuelve false si ninguno está marcado
        })
        .test("only-one-checked", i18n.get("forms.validation.only.one.checkbox"), (values) => {
            const { swift, aba } = values;
            return !(swift && aba); // Devuelve false si ambos están marcados
        });

    const [data, setData] = useState(initialValues);

    useEffect(() => {
        let countryName = "";
        if (data.country) {
            const selectedCountry = comexCountries.find((c) => c.id === data.country);
            countryName = selectedCountry?.description || "";
        }

        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                contactDestination: {
                    ...data,
                    countryName,
                },
            }),
        );
    }, [data]);

    const handleSubmit = () => {
        dispatch(push("/comex/concept"));
    };

    const DestinationInfo = () => (
        <Drawer
            className="help-slider"
            openSidebar={showDestinationInfo}
            toggleSidebar={() => setShowDestinationInfo(false)}>
            <div className="container">
                <div
                    className="row"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: i18n.get("comex.destination.info.content"),
                    }}
                />
            </div>
        </Drawer>
    );

    const AccountNumberInfo = () => (
        <Drawer
            className="help-slider"
            openSidebar={showAccountNumberInfo}
            toggleSidebar={() => setShowAccountNumberInfo(false)}>
            <div className="container">
                <div
                    className="row"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: i18n.get("comex.iban.info.content"),
                    }}
                />
            </div>
        </Drawer>
    );

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={0} />}

            <Notification scopeToShow="CreateComexContact" />
            <DestinationInfo />
            <AccountNumberInfo />
            <ResponsiveHeader id="comex.create.contactDestination.title" backLinkTo="/comex" />

            <MainContainer showLoader={fetching}>
                {!isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={0} />}

                <Formik initialValues={initialValues} validationSchema={newContactSchema}>
                    {({ values, handleChange, setFieldValue }) => {
                        setData(values);
                        const canSubmit =
                            !values.recipientName ||
                            !values.iban ||
                            !values.country ||
                            !values.number ||
                            !values.currency ||
                            !(values.swift || values.aba) ||
                            (values.swift && !/^[a-zA-Z0-9]{8,11}$/.test(values.number)) ||
                            (values.aba && !/^\d{9}$/.test(values.number)) ||
                            !values.address ||
                            !values.countryCode;

                        const addContact = values.add;
                        return (
                            <>
                                <Form onChange={handleChange}>
                                    <GridTable className="form">
                                        <GridTable.Container columns={12} rows={2} columnsTemplate="fixed">
                                            <GridTable.Data columnStart={2} columnWidth={5} rowStart={1}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="recipientName"
                                                    hidePlaceholder
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={1}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="iban"
                                                    hidePlaceholder
                                                    showTooltip
                                                    onTooltipClick={() => setShowAccountNumberInfo(true)}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data
                                                flexDirection="column"
                                                columnStart={2}
                                                columnWidth={5}
                                                rowStart={2}>
                                                <CustomText
                                                    className="data-label"
                                                    text={`${FORM_ID}.destination.label`}
                                                    textType="I18N"
                                                    showTooltip
                                                    onTooltipClick={() => setShowDestinationInfo(true)}
                                                />
                                                <div className="flex gap-6">
                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name="swift"
                                                        labelTextProps={{ defaultValue: "SWIFT" }}
                                                        style={{ marginRight: "16px" }}
                                                        onChange={(e) => handleCheckboxChange(e, setFieldValue, "aba")}
                                                    />

                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name="aba"
                                                        labelTextProps={{ defaultValue: "ABA" }}
                                                        onChange={(e) => handleCheckboxChange(e, setFieldValue, "swift")}
                                                    />
                                                </div>
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={2}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="address"
                                                    hidePlaceholder
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={2} columnWidth={5} rowStart={3}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="number"
                                                    hidePlaceholder
                                                    labelText={selectedBankType != null ? `${FORM_ID}.bankType.${selectedBankType?.toLowerCase()}.label` : `${FORM_ID}.number.label`}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={3}>
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="country"
                                                    options={comexCountries.map(({ id, description }) => ({
                                                        value: id,
                                                        label: description,
                                                    }))}
                                                    placeholder=" "
                                                    value={null}
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={2} columnWidth={5} rowStart={4}>
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="currency"
                                                    options={comexCurrencies.map(({ id, description }) => ({
                                                        value: id,
                                                        label: description,
                                                    }))}
                                                    placeholder=" "
                                                    value={null}
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={4}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="countryCode"
                                                    hidePlaceholder
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={5}>
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="add"
                                                    labelTextProps={{ defaultValue: "add" }}
                                                    labelClassName="checkbox-add-contact"
                                                    style={{ marginRight: "16px" }}
                                                />
                                            </GridTable.Data>
                                        </GridTable.Container>
                                    </GridTable>
                                </Form>
                                {addContact ? (
                                    <GeneralCredentialForm
                                        transactionData={comexTransfer?.contactDestination}
                                        action={comexActions.createComexContact}
                                        idActivity="create.comex.contact"
                                        hideIndications
                                        isDisabled={canSubmit}
                                    />
                                ) : (
                                    <Row className={`form-actions `}>
                                        <Col
                                            xs={12}
                                            md={4}
                                            mdOffset={6}
                                            lg={3}
                                            lgOffset={6}
                                            className="align-items-end">
                                            <div className="form-group hidden-label">
                                                <div className="button-group">
                                                    <Button
                                                        className="btn btn-primary"
                                                        onClick={() => handleSubmit(values)}
                                                        label="global.continue"
                                                        disabled={canSubmit}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};
ComexNewContactPage.propTypes = {
    isDesktop: bool.isRequired,
    fetching: bool.isRequired,
};

const mapStateToProps = (state) => ({
    fetching: comexSelectors.getFetching(state),
});

export default connect(mapStateToProps)(ComexNewContactPage);
